import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const Page = styled.main`
  background: #fafaf7;
  min-height: 100vh;
  padding-top: 120px;
  padding-bottom: 120px;

  @media screen and (max-width: 768px) {
    padding-top: 100px;
    padding-bottom: 80px;
  }
`;

export const MaxWidth = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 50px;

  @media screen and (max-width: 768px) {
    padding: 0 22px;
  }
`;

export const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #4a4a4a;
  text-decoration: none;
  margin-bottom: 28px;
  transition: color 0.2s ease;

  &:hover {
    color: ${(props) => props.theme.text};
  }
`;

export const Hero = styled.section`
  padding: 0 0 60px;
  border-bottom: 1px solid #e8e5de;
  margin-bottom: 60px;
`;

export const Eyebrow = styled(motion.h2)`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${(props) => props.theme.text};
  margin-bottom: 14px;
`;

export const Title = styled(motion.h1)`
  font-size: 64px;
  font-weight: 700;
  line-height: 1.05;
  color: #0f1115;
  margin: 0 0 20px;
  font-family: 'Poppins', sans-serif;
  max-width: 820px;

  em {
    font-style: italic;
    font-weight: 500;
    color: ${(props) => props.theme.text};
  }

  @media screen and (max-width: 768px) {
    font-size: 38px;
  }
`;

export const Lead = styled.p`
  font-size: 17px;
  line-height: 1.65;
  color: #4a4a4a;
  max-width: 680px;
  margin: 0 0 30px;
`;

export const Metrics = styled.div`
  display: flex;
  gap: 36px;
  flex-wrap: wrap;
`;

export const Metric = styled.div`
  strong {
    display: block;
    font-size: 36px;
    font-weight: 700;
    color: #0f1115;
    font-family: 'Poppins', sans-serif;
    line-height: 1;
  }
  span {
    display: block;
    font-size: 12px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #6b6b6b;
    margin-top: 6px;
  }
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2dfd7;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #6b6b6b;
  }
`;

export const FeaturedSection = styled.section`
  margin-bottom: 80px;
`;

export const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 22px;
`;

export const FeaturedCard = styled(motion.button)`
  position: relative;
  border: none;
  padding: 0;
  border-radius: 20px;
  overflow: hidden;
  aspect-ratio: 4 / 5;
  cursor: pointer;
  background: #1a1a1a;
  box-shadow: 0 18px 40px -28px rgba(0, 0, 0, 0.5);
  transition: transform 0.35s ease, box-shadow 0.35s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 26px 60px -30px ${(props) => props.theme.text};
  }

  &:hover img {
    transform: scale(1.06);
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s ease;
  }
`;

export const FeaturedOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0.4) 60%,
    rgba(0, 0, 0, 0.85) 100%
  );
`;

export const FeaturedBody = styled.div`
  position: absolute;
  left: 22px;
  right: 22px;
  bottom: 22px;
  color: #fff;
  text-align: left;
`;

export const FeaturedFlag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #0f1115;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

export const FeaturedTitle = styled.h3`
  font-size: 18px;
  line-height: 1.35;
  margin: 0;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

export const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.text : '#d8dbe0')};
  background: ${({ $active, theme }) => ($active ? theme.text : '#ffffff')};
  color: ${({ $active }) => ($active ? '#fff' : '#424a57')};

  &:hover {
    border-color: ${(props) => props.theme.text};
  }
`;

export const GridSection = styled.section`
  margin-top: 20px;
`;

export const EmptyState = styled.p`
  text-align: center;
  color: #6b6b6b;
  padding: 60px 0;
  font-size: 14px;
`;

// Şehir + bayrak rozeti — masonry'de her fotonun sağ altına oturur.
// react-photo-album'ün .react-photo-album--photo wrapper'ı position: relative.
export const PhotoBadge = styled.span`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: calc(100% - 16px);
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(15, 17, 21, 0.62);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  opacity: 0.9;
  transition: opacity 0.25s ease;

  .react-photo-album--photo:hover & {
    opacity: 1;
  }

  @media screen and (max-width: 600px) {
    right: 6px;
    bottom: 6px;
    padding: 3px 7px;
    font-size: 9px;
    letter-spacing: 0.4px;
  }
`;
